import { useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { LogoMark, ThemeToggle } from '@/components/layout/AppHeader'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'
import { TagIcon } from '@/features/billing/CouponField'
import { captureCouponFromUrl, readStoredCoupon } from '@/features/billing/coupon'
import { useAuthStore } from '@/store/auth'
import { toast } from '@/store/toast'

type Mode = 'login' | 'register'

/**
 * Sign in / create account.
 *
 * One column on phones (logo, heading, form, switch link); on wider screens
 * the same column sits in a 420px card on the canvas. No marketing panel:
 * people land here every day, so it should feel like the door to a tool.
 */
export function AuthPage({ mode }: { mode: Mode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, register, status, onboarding } = useAuthStore()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<ApiError | null>(null)
  const [busy, setBusy] = useState(false)
  // `?coupon=CODE` on this link (or one saved from an earlier link) is applied at checkout.
  const [coupon] = useState(() => captureCouponFromUrl(location.search) ?? readStoredCoupon())

  if (status === 'authenticated') {
    const target = onboarding?.isComplete ? '/dashboard' : '/onboarding'
    return <Navigate to={target} replace />
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const session =
        mode === 'login'
          ? await login(email, password)
          : await register(email, password, fullName || undefined)

      const from = (location.state as { from?: string } | null)?.from
      navigate(session.onboarding.isComplete ? (from ?? '/dashboard') : '/onboarding', {
        replace: true,
      })
    } catch (caught) {
      const apiError = caught instanceof ApiError ? caught : null
      setError(apiError)
      if (apiError && Object.keys(apiError.fieldErrors).length === 0) {
        toast.error(mode === 'login' ? 'Could not sign in' : 'Could not create the account',
          apiError.message)
      }
    } finally {
      setBusy(false)
    }
  }

  const isRegister = mode === 'register'
  const formError = error && Object.keys(error.fieldErrors).length === 0 ? error.message : null

  return (
    <div className="relative flex min-h-dvh flex-col bg-canvas">
      <GridBackdrop />

      <div className="relative mx-auto flex h-14 w-full max-w-[1280px] items-center justify-end px-2 sm:px-6" style={{ paddingTop: 'var(--safe-top)' }}>
        <ThemeToggle />
      </div>

      <main className="relative flex flex-1 flex-col items-center px-4 pb-10 sm:justify-center sm:px-6 sm:pb-16">
        <div className="grid w-full max-w-[1000px] items-center gap-10 lg:grid-cols-[1fr_440px] lg:gap-16">
        <aside className="hidden lg:block">
          <p className="label-eyebrow text-brand">A clearer path to your next role</p>
          <h2 className="mt-5 text-[44px] font-semibold leading-[1.12] tracking-[-0.04em] text-ink">Less paperwork.<br /><span className="text-brand">More possibility.</span></h2>
          <p className="mt-5 max-w-sm text-[16px] leading-relaxed text-ink-muted">Keep your profile, applications, and interview preparation connected in one workspace.</p>
          <ol className="mt-8 space-y-5">
            {[['Discover', 'Find roles that fit your experience.'], ['Apply', 'Review your answers and follow each application.'], ['Prepare', 'Practice for the conversations ahead.']].map(([title, description], index) => (
              <li key={title} className="flex items-start gap-3.5">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-brand/15 bg-brand-soft text-[13px] font-semibold text-brand">0{index + 1}</span>
                <div><p className="text-[14px] font-semibold text-ink">{title}</p><p className="mt-0.5 text-[13px] text-ink-muted">{description}</p></div>
              </li>
            ))}
          </ol>
        </aside>
        <div className="card mx-auto w-full max-w-[440px] animate-fade-in px-5 py-7 sm:px-8 sm:py-8">
          <div className="flex items-center gap-2.5">
            <LogoMark className="h-8 w-8 rounded-[9px]" />
            <span className="text-[17px] font-semibold tracking-tight text-ink">JobSlide</span>
          </div>
          <p className="mt-3 text-[13.5px] leading-snug text-ink-muted">
            Your profile, applications, and next opportunity. All in one place.
          </p>

          <h1 className="mt-7 text-[24px] font-semibold leading-tight tracking-[-0.02em] text-ink sm:mt-8">
            {isRegister ? 'Create your account' : 'Welcome back'}
          </h1>

          {isRegister && coupon && (
            <p className="mt-4 flex items-center gap-2 rounded-field border border-success/25 bg-success/[0.07] px-3 py-2 text-[13px] text-ink-soft" role="status">
              <TagIcon className="h-4 w-4 shrink-0 text-success" />
              <span>
                Coupon <span className="num font-semibold text-ink">{coupon}</span> will be applied at checkout.
              </span>
            </p>
          )}

          <form onSubmit={submit} className="mt-5 space-y-4" noValidate>
            {isRegister && (
              <TextField
                label="Full name"
                appearance="box"
                className="h-12"
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                placeholder="Your name"
                autoComplete="name"
                autoCapitalize="words"
                error={error?.fieldErrors.fullName}
              />
            )}

            <TextField
              label="Email"
              appearance="box"
              className="h-12"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              autoFocus
              error={error?.fieldErrors.email}
            />

            <TextField
              label="Password"
              appearance="box"
              className="h-12 !pr-[76px]"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={isRegister ? 'At least 12 characters' : 'Your password'}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
              required
              hint={isRegister ? 'Upper and lower case, a number and a symbol.' : undefined}
              error={error?.fieldErrors.password}
              trailing={
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-pressed={showPassword}
                  className="mr-1.5 inline-flex h-11 items-center rounded-[8px] px-2.5 text-[13px] font-medium text-ink-muted transition-colors hover:text-ink"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              }
            />

            {formError && (
              <p role="alert" className="rounded-field border border-danger/25 bg-danger/[0.07] px-3.5 py-2.5 text-[14px] text-danger">
                {formError}
              </p>
            )}

            <div className="pt-1">
              <Button type="submit" size="lg" fullWidth loading={busy}>
                {isRegister ? 'Create account' : 'Sign in'}
              </Button>
            </div>
          </form>

          <p className="mt-5 flex min-h-[44px] flex-wrap items-center justify-center gap-x-1.5 border-t border-line pt-4 text-center text-[14px] text-ink-muted">
            {isRegister ? 'Already have an account?' : "Don't have an account?"}
            <Link
              to={isRegister ? '/login' : '/register'}
              className="inline-flex min-h-[44px] items-center px-1 font-medium text-ink underline underline-offset-4 transition-colors hover:text-brand"
            >
              {isRegister ? 'Sign in' : 'Create one'}
            </Link>
          </p>
        </div>

        </div>
        <p className="mt-6 max-w-[420px] text-center text-[12.5px] leading-relaxed text-ink-faint">
          <LockIcon />
          Your answers are encrypted and only used on applications you approve.
        </p>
      </main>
    </div>
  )
}

/** The faint grid the 404 page uses, fading out towards the edges. Desktop only. */
function GridBackdrop() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 hidden sm:block"
      style={{
        backgroundImage:
          'linear-gradient(rgb(var(--brand) / 0.07) 1px, transparent 1px), linear-gradient(90deg, rgb(var(--brand) / 0.07) 1px, transparent 1px)',
        backgroundSize: '36px 36px',
        maskImage: 'radial-gradient(ellipse 50% 45% at 50% 50%, #000 20%, transparent 100%)',
        WebkitMaskImage: 'radial-gradient(ellipse 50% 45% at 50% 50%, #000 20%, transparent 100%)',
      }}
    />
  )
}

function LockIcon() {
  return (
    <svg className="mr-1.5 inline-block h-3.5 w-3.5 align-[-2px]" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="3" y="7" width="10" height="7" rx="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  )
}
